import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LoginPage from "../../../Login/LoginPage";
import {
  Card,
  Space,
  Descriptions,
  message,
  Tag,
  Spin,
  Alert,
  Typography,
  Button,
} from "antd";
import {
  isLogged,
  destroyAuthToken,
  getAuthToken,
} from "../../../../helpers/account";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { api } from "../../../../config/api";
import EditAccountForm from "./Forms/EditAccountForm";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";

const { Title, Text } = Typography;

function AccountDataPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const accountDataChanged = useSelector((state) => state.accountDataChanged);

  useEffect(() => {
    const apiUrl = `${api.host}/accounts/${sessionStorage.getItem("uuid")}`;

    if (sessionStorage.getItem("authToken")) {
      fetch(apiUrl, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `${getAuthToken()}`,
        }),
      })
        .then((response) => response.json())
        .then((fetchedData) => {
          setData(fetchedData);
          setLoading(false);
        })
        .catch((error) => {
          setTimeout(() => {
            message.error(t('transactions.loggedOutSecurity'));
            destroyAuthToken();
            window.location.href = "/transactions";
          }, 2000);
          setLoading(false);
        });
    }
  }, [accountDataChanged]);

  if (isLogged()) {
    return (
      <>
        <Helmet>
          <title>{t('dashboard.account.title')}</title>
        </Helmet>
        
        <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "40px 20px" }}>
          <Spin spinning={loading}>
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              {/* Header */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
                  <Link to="/account">
                    <Button icon={<ArrowLeftOutlined />} type="text">
                      {t('dashboard.account.return')}
                    </Button>
                  </Link>
                  <Title level={2} style={{ margin: 0, color: "#2c3e50" }}>
                    {t('dashboard.account.accountData')}
                  </Title>
                </div>
                <Text style={{ color: "#6c757d", fontSize: "16px" }}>
                  {t('dashboard.account.managePersonalData')}
                </Text>
              </div>

              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Info Alert */}
                <Alert
                  type="info"
                  message={t('dashboard.account.publicProfileInfo')}
                  style={{ borderRadius: "8px" }}
                />

                {/* Edit Form */}
                <EditAccountForm />

                {/* Account Details */}
                <Card
                  title={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>👤</span>
                      <span>{t('dashboard.account.accountDetails')}</span>
                    </div>
                  }
                  style={{
                    border: "none",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    borderRadius: "12px",
                  }}
                >
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {/* Account Type & Representative */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.accountType')}</Text>
                        <div style={{ marginTop: "4px" }}>
                          <Tag color={data.is_company ? "blue" : "green"}>
                            {data.is_company ? t('dashboard.account.company') : t('dashboard.account.privatePerson')}
                          </Tag>
                        </div>
                      </div>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.accountingPartner')}</Text>
                        <div style={{ marginTop: "4px" }}>
                          <Text>{data?.accountant?.name || t('dashboard.account.none')}</Text>
                        </div>
                      </div>
                    </div>

                    {/* Representative */}
                    <div>
                      <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.representative')}</Text>
                      <div style={{ marginTop: "4px" }}>
                        <Text>{data.fullname || t('dashboard.account.notProvided')}</Text>
                      </div>
                    </div>

                    {/* Address & Company Info */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.headquarters')}</Text>
                        <div style={{ marginTop: "4px" }}>
                          <Text>
                            {data.address || t('dashboard.account.notProvided')} {data.post_code} {data.city}
                          </Text>
                        </div>
                      </div>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.nipAndCompanyName')}</Text>
                        <div style={{ marginTop: "4px" }}>
                          <Text>
                            {data.nip || t('dashboard.account.notProvided')} {data.company_name}
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.phone')}</Text>
                        <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                          <Text>{data.mobile || t('dashboard.account.notProvided')}</Text>
                          <Tag color={data.mobile_confirmed ? "success" : "warning"}>
                            {data.mobile_confirmed ? t('dashboard.account.verified') : t('dashboard.account.unverified')}
                          </Tag>
                        </div>
                      </div>
                      <div>
                        <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.email')}</Text>
                        <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                          <Text>{data.email}</Text>
                          <Tag color={data.email_confirmed ? "success" : "warning"}>
                            {data.email_confirmed ? t('dashboard.account.verified') : t('dashboard.account.unverified')}
                          </Tag>
                        </div>
                      </div>
                    </div>

                    {/* Access Level */}
                    <div>
                      <Text strong style={{ color: "#2c3e50" }}>{t('dashboard.account.accessLevel')}</Text>
                      <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                        {data.access_level === 1 ? (
                          <>
                            <Tag color="purple">{t('dashboard.account.employee')}</Tag>
                            <Link to="/admin/transactions" style={{ color: "#667eea" }}>
                              {t('dashboard.account.employeeTools')}
                            </Link>
                          </>
                        ) : (
                          <Tag color="success">{t('dashboard.account.regularUser')}</Tag>
                        )}
                      </div>
                    </div>
                  </Space>
                </Card>

                {/* Help Card */}
                <Card 
                  title={`💡 ${t('dashboard.account.tip')}`}
                  style={{ 
                    borderRadius: "12px", 
                    border: "none", 
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)", 
                    background: "#f8f9fa" 
                  }}
                >
                  <Text style={{ color: "#6c757d", fontSize: "14px", lineHeight: "1.6" }}>
                    {t('dashboard.account.keepDataUpdatedTip')}
                  </Text>
                </Card>
              </Space>
            </div>
          </Spin>
        </div>
      </>
    );
  } else {
    return <LoginPage />;
  }
}

export default AccountDataPage;